/**
 * The combined business overview — memberships, bookings and gifts.
 *
 * From docs/booking-api.swagger.json (`OverviewDashboardDto`). The page answers
 * three questions on two different bases:
 *
 *   Money     what came in, counted on the day the money arrived
 *   Activity  what happened, counted on the day of the class
 *   People    who did it, counted on the day of the class
 *
 * Mixing those on one screen is how somebody ends up comparing a payment-date
 * total to a class-date total and concluding the page is broken, which is why
 * each tab states its own basis at the top.
 */

/** `OverviewMetric` — decides which rows come back when a tile is opened. */
export const METRIC = {
  collected: 0,
  paidOnline: 1,
  paidOffline: 2,
  refunded: 3,
  owedToYou: 4,
  noPayment: 5,
  benefitValueGiven: 6,
  voided: 7,
  recurringRevenue: 8,
  oneOffRevenue: 9,
  autoRenewing: 10,
  // Activity tab. Bookings are listed by class date, the basis the tab counts on.
  activeMemberships: 11,
  expiringSoon: 12,
  bookings: 13,
  checkedIn: 14,
  benefitBookings: 15,
  uniqueCustomers: 16,
  recentlyExpired: 17,
} as const;

/**
 * The activity tab's metrics. Every tile there is a count of memberships,
 * bookings or people, and its rows carry no amount — a price beside them would
 * be a figure the tab shows nowhere else, on a tab that is not about money.
 */
export const ACTIVITY_METRICS: number[] = [
  METRIC.activeMemberships,
  METRIC.expiringSoon,
  METRIC.recentlyExpired,
  METRIC.bookings,
  METRIC.checkedIn,
  METRIC.benefitBookings,
  METRIC.uniqueCustomers,
];

/**
 * The metrics whose tile shows a count rather than an amount. For these the
 * row count is the figure the reader is checking against, so it leads and the
 * money follows — a drawer headlining "$3,120" under a tile that said "14"
 * reads as a mismatch even when both are right.
 */
export const COUNT_METRICS: number[] = [
  METRIC.noPayment,
  METRIC.autoRenewing,
  ...ACTIVITY_METRICS,
];

/** `OverviewRevenueSource`. */
export const SOURCE = { memberships: 0, bookings: 1, gifts: 2, vouchers: 3 } as const;

export const SOURCE_LABEL: Record<number, string> = {
  [SOURCE.memberships]: 'overview.memberships',
  [SOURCE.bookings]: 'overview.bookings',
  [SOURCE.gifts]: 'overview.gifts',
  [SOURCE.vouchers]: 'overview.vouchers',
};

export function sourceLabelKey(source: number | null | undefined) {
  return SOURCE_LABEL[source ?? -1] ?? 'overview.other';
}

/**
 * One CSS variable per source, so the stacked bar, the tags and the meters all
 * take their colour from the theme rather than three copies of a hex code that
 * only worked on a white page.
 */
export const SOURCE_VAR: Record<number, string> = {
  [SOURCE.memberships]: 'var(--chart-1)',
  [SOURCE.bookings]: 'var(--chart-2)',
  [SOURCE.gifts]: 'var(--chart-3)',
  [SOURCE.vouchers]: 'var(--chart-4)',
};

export interface OverviewMoney {
  collected: number | null;
  collectedOnline: number | null;
  collectedOffline: number | null;
  refunded: number | null;
  voided: number | null;
  net: number | null;
  /** Unpaid balances on live bookings as at today — a position, never a period. */
  outstanding: number | null;
  /** List price of active memberships that were never billed at all. */
  unbilled: number | null;
  benefitValueGiven: number | null;
  /** Revenue a location filter silently drops, because plans need no location. */
  excludedNoLocation: number | null;
  noPaymentBookings: number | null;
  noPaymentValue: number | null;
  compedBookings: number | null;
  needsReviewBookings: number | null;
  payments: number | null;
  averagePayment: number | null;
}

export interface OverviewSourceBreakdown {
  id: string;
  nameEn: string | null;
  nameEs: string | null;
  collected: number | null;
  refunded: number | null;
  net: number | null;
  payments: number | null;
  /** Share of this source, not of the whole business. */
  share: number | null;
}

export interface OverviewSource {
  source: number;
  collected: number | null;
  refunded: number | null;
  net: number | null;
  payments: number | null;
  share: number | null;
  previousCollected: number | null;
  /** Null when the previous period was zero — there is no percentage to state. */
  change: number | null;
  breakdown: OverviewSourceBreakdown[] | null;
}

export interface OverviewChannel {
  method: number;
  isOffline: boolean;
  collected: number | null;
  refunded: number | null;
  net: number | null;
  payments: number | null;
  share: number | null;
}

export interface OverviewRecurring {
  recurringCollected: number | null;
  recurringPayments: number | null;
  oneOffCollected: number | null;
  oneOffPayments: number | null;
  recurringShare: number | null;
  activeAutoRenewing: number | null;
}

export interface OverviewDay {
  date: string;
  memberships: number | null;
  bookings: number | null;
  gifts: number | null;
  vouchers: number | null;
  total: number | null;
}

export interface OverviewActivity {
  activeMemberships: number | null;
  newMemberships: number | null;
  renewals: number | null;
  expiringSoon: number | null;
  /** Ended in the last 14 days, not renewed, no other live membership. A position, not a period. */
  recentlyExpired: number | null;
  bookings: number | null;
  bookingsCheckedIn: number | null;
  checkInRate: number | null;
  benefitBookings: number | null;
  uniqueCustomers: number | null;
  eventsBooked: number | null;
}

export interface OverviewPlan {
  subscriptionId: string;
  nameEn: string | null;
  nameEs: string | null;
  purchases: number | null;
  new: number | null;
  renewals: number | null;
  revenue: number | null;
  activeNow: number | null;
  share: number | null;
}

export interface OverviewEvent {
  eventId: string;
  name: string | null;
  locationName: string | null;
  price: number | null;
  bookings: number | null;
  paid: number | null;
  /** A class packed with members is popular and earns nothing directly. */
  usedBenefit: number | null;
  revenue: number | null;
  checkedIn: number | null;
  checkInRate: number | null;
  slots: number | null;
  fillRate: number | null;
}

export interface OverviewMember {
  userId: string;
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
  planNameEn: string | null;
  planNameEs: string | null;
  membershipEndDate: string | null;
  benefitsUsed: number | null;
  bookings: number | null;
  bookingsElapsed: number | null;
  checkedIn: number | null;
  checkInRate: number | null;
  spent: number | null;
  lastMembershipEndDate: string | null;
  overPlanPrice: number | null;
  firstMembershipStartDate: string | null;
  spentBeforeMembership: number | null;
  bookingsBeforeMembership: number | null;
  benefitsUsedSinceMembership: number | null;
}

export interface OverviewMembers {
  /** Below this many bookings a check-in rate is noise, so the list says so. */
  minBookingsForRates: number | null;
  topBenefitUsers: OverviewMember[] | null;
  unusedBenefits: OverviewMember[] | null;
  mostConsistent: OverviewMember[] | null;
  paidButNoShow: OverviewMember[] | null;
  dropInsToConvert: OverviewMember[] | null;
  convertedCustomers: OverviewMember[] | null;
  comparablePlanNameEn: string | null;
  comparablePlanNameEs: string | null;
  comparablePlanPrice: number | null;
}

/** The keys of `OverviewMembers` that hold a list. */
export type PeopleListKey =
  | 'topBenefitUsers'
  | 'unusedBenefits'
  | 'mostConsistent'
  | 'paidButNoShow'
  | 'dropInsToConvert'
  | 'convertedCustomers';

export interface OverviewDashboard {
  startDate: string | null;
  endDate: string | null;
  previousStartDate: string | null;
  previousEndDate: string | null;
  money: OverviewMoney | null;
  bySource: OverviewSource[] | null;
  byChannel: OverviewChannel[] | null;
  recurring: OverviewRecurring | null;
  series: OverviewDay[] | null;
  activity: OverviewActivity | null;
  plans: OverviewPlan[] | null;
  events: OverviewEvent[] | null;
  members: OverviewMembers | null;
}

/** `OverviewRowDto` — one line behind a tile. */
export interface OverviewRow {
  id: string;
  kind: number;
  when: string | null;
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
  what: string | null;
  amount: number | null;
  method: number | null;
  isOffline: boolean;
  isRenewal: boolean;
}

/** `PagedOverviewRowsDto`. `sum` is the whole set, not the page. */
export interface PagedOverviewRows {
  total: number | null;
  page: number | null;
  size: number | null;
  sum: number | null;
  rows: OverviewRow[] | null;
}
